// user-dashboard/components/PersonaCheckModal.jsx
import React, { useEffect, useState } from "react";

const STAFF_QUESTIONS = [
  { key: "password_reset", label: "Did it ask for a password reset?" },
  { key: "verify_number", label: "Did it ask to verify the number?" },
  { key: "pop_up_straight_away", label: "Did it pop up straight away?" },
  { key: "good_ml_profile", label: "Good ML Profile?" },
];

const BUYER_QUESTIONS = [
  { key: "password_reset", label: "Did it ask for a password reset?" },
  { key: "verify_number", label: "Did it ask to verify the number?" },
  { key: "pop_up_straight_away", label: "Did it pop up straight away?" },
  { key: "queued_non_uk", label: "Have you queued for a Non-UK event recently for this email?" },
];

const ANSWER_LABELS = {
  password_reset: "Did it ask for a password reset?",
  verify_number: "Did it ask to verify the number?",
  pop_up_straight_away: "Did it pop up straight away?",
  good_ml_profile: "Good ML Profile?",
  queued_non_uk: "Have you queued for a Non-UK event recently for this email?",
};

function YesNoRow({ label, name, value, onChange, readOnly }) {
  if (readOnly) {
    return (
      <div className="mb-2">
        <div className="fw-semibold small">{label}</div>
        <div>{value === "yes" ? "Yes" : value === "no" ? "No" : "—"}</div>
      </div>
    );
  }

  return (
    <fieldset className="mb-3">
      <legend className="form-label fw-semibold small mb-1">{label}</legend>
      <div className="d-flex gap-3">
        {["yes", "no"].map((opt) => (
          <label key={opt} className="form-check-label d-flex align-items-center gap-1" style={{ cursor: "pointer" }}>
            <input
              className="form-check-input m-0"
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
            />
            {opt === "yes" ? "Yes" : "No"}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Modal for filling or reviewing persona-check answers.
 * Stamp metadata (Buyer/Staff, who, time) comes from the user session, not the form.
 *
 * Props:
 * - open, onClose
 * - record: advice password row
 * - mode: "edit" | "view"
 * - sessionStampedAs: "Buyer" | "Staff" from current session role
 * - sessionWho: user_code / identity from session
 * - onSubmit({ stampedAs, stampedBy, answers }) => Promise
 */
export default function PersonaCheckModal({
  open,
  onClose,
  record,
  mode = "edit",
  sessionStampedAs = "Buyer",
  sessionWho = "",
  onSubmit,
}) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const readOnly = mode === "view";
  const stampedAs = readOnly
    ? (record?.persona_check_stamped_as || sessionStampedAs || "Buyer")
    : (sessionStampedAs || "Buyer");
  const stampedBy = readOnly
    ? (record?.persona_check_stamped_by || sessionWho || "")
    : (sessionWho || "");
  const questions = stampedAs === "Staff" ? STAFF_QUESTIONS : BUYER_QUESTIONS;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setAnswers(readOnly && record ? (record.persona_check_answers || {}) : {});
  }, [open, readOnly, record]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) {
      onClose?.();
      return;
    }

    for (const q of questions) {
      if (!answers[q.key]) {
        setError(`Please answer: ${q.label}`);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit?.({
        stampedAs,
        stampedBy,
        answers,
      });
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save persona check");
    } finally {
      setSubmitting(false);
    }
  };

  const triggeredOn = record?.persona_check_triggered_on
    ? new Date(record.persona_check_triggered_on).toLocaleString()
    : "—";

  return (
    <div
      className="modal fade show"
      style={{ display: "block", background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-scrollable" onClick={stop}>
        <div className="modal-content" style={{ fontSize: "1rem" }}>
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">
                {readOnly ? "Persona Check Answers" : "Persona Check"}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="mb-2 text-muted small">
                <strong>Site:</strong> {record?.site || "—"} &nbsp;|&nbsp;
                <strong>Email:</strong> {record?.email || "—"}
              </div>

              {readOnly && (
                <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
                  <div><strong></strong> {stampedBy || "—"}</div>
                  <div><strong></strong> {triggeredOn}</div>
                </div>
              )}

              {readOnly
                ? Object.keys(ANSWER_LABELS)
                    .filter((k) => answers?.[k] != null)
                    .map((key) => (
                      <YesNoRow
                        key={key}
                        label={ANSWER_LABELS[key]}
                        name={key}
                        value={answers[key]}
                        readOnly
                      />
                    ))
                : questions.map((q) => (
                    <YesNoRow
                      key={q.key}
                      label={q.label}
                      name={q.key}
                      value={answers[q.key]}
                      onChange={(v) => setAnswer(q.key, v)}
                    />
                  ))}

              {error && (
                <div className="alert alert-danger py-2 small mb-0">{error}</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onClose}
                disabled={submitting}
              >
                {readOnly ? "Close" : "Cancel"}
              </button>
              {!readOnly && (
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={submitting}
                >
                  {submitting ? "Saving…" : "Submit"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
